import { useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { GenieLoader } from "@/components/ui/GenieLoader";
import { suiteLandingPath } from "@/components/layout/navConfig";
import { mintSuiteSsoUrl, resolveSuiteEntryUrl } from "@/lib/suite/openSuiteApp";
import { activityService } from "@/services/analytics/ActivityService";
import { ImpersonationService } from "@/services/users/ImpersonationService";
import { useSuiteManifest } from "@/hooks/suite";
import { supabase } from "@/integrations/supabase/client";

/**
 * /open/:appId — the SSO hub route for the Help Genie app family.
 *
 * Satellite apps (and the switcher's shareable links) launch each other by
 * pointing here, e.g. /open/outbound?source=switcher. Voice is the only app
 * that knows how to mint SSO tokens, so even a Mail → Outbound hop routes
 * through this page: resolve the app from the Suite manifest, mint the
 * tokened URL, and redirect in place.
 *
 * NOTE: /launch/:packId was already taken by public activation — keep this
 * route at /open.
 *
 * `force_logout=1` lets a satellite app's own logout bounce through here to
 * drop the HG Voice session too, before minting a fresh (unauthenticated)
 * SSO redirect.
 *
 * `end_impersonation=1` lets a satellite app's "exit impersonation" bounce
 * through here to drop this tab's impersonation state first, so the minted
 * token is the admin's own rather than the impersonated user's again.
 */
const OpenSuiteAppPage = () => {
  const { appId } = useParams<{ appId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { data: apps } = useSuiteManifest();
  const startedRef = useRef(false);

  const app = apps?.find((a) => a.id === appId?.toLowerCase());

  useEffect(() => {
    if (!apps || startedRef.current) return;

    // Unknown app → Suite directory; the home app (Voice) → its own dashboard.
    if (!app?.externalUrl) {
      navigate(app?.home ? "/dashboard" : suiteLandingPath, { replace: true });
      return;
    }
    startedRef.current = true;

    const { externalUrl, ssoEntryPath, productWord } = app;
    const source = searchParams.get("source") ?? "open-route";
    const forceLogout = searchParams.get("force_logout") === "1";
    const endImpersonation = searchParams.get("end_impersonation") === "1";
    activityService.logSuiteAppVisit(productWord, externalUrl, source);

    (async () => {
      if (forceLogout) {
        await supabase.auth.signOut();
        window.location.replace(resolveSuiteEntryUrl(externalUrl, ssoEntryPath));
        return;
      }
      if (endImpersonation) ImpersonationService.clearImpersonationState();
      window.location.replace(await mintSuiteSsoUrl(externalUrl, ssoEntryPath));
    })();
  }, [apps, app, navigate, searchParams]);

  return (
    <GenieLoader
      size="fullscreen"
      loadingText={app ? `Opening Help Genie ${app.productWord}…` : "Opening…"}
    />
  );
};

export default OpenSuiteAppPage;
